class AppIDB {
  private readonly versionKey;

  private readonly databaseKey;

  private readonly objectStoreKey;

  public constructor(objectStoreKey = 'permanent_cache', databaseKey = 'ioSuiteStorage', versionKey = 1) {
    this.objectStoreKey = objectStoreKey;
    this.databaseKey = databaseKey;
    this.versionKey = versionKey;
  }

  private async getDatabase() {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = window.indexedDB.open(this.databaseKey, this.versionKey);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(this.objectStoreKey)) {
          const store = db.createObjectStore(this.objectStoreKey, { keyPath: 'key' });
          store.createIndex('byKey', 'key');
        }
      };

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        const db = request.result as IDBDatabase;

        db.onversionchange = () => {
          db.close();

          window.location.reload();
        };

        resolve(db);
      };
    });
  }

  public async set(key: string, data: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.getDatabase().then((database) => {
        if (!database.objectStoreNames.contains(this.objectStoreKey)) {
          database.close();
          resolve();
          return;
        }

        const transaction = database.transaction(this.objectStoreKey, 'readwrite');
        const store = transaction.objectStore(this.objectStoreKey);
        const request = store.put({
          key,
          value: data,
        });

        transaction.oncomplete = () => {
          database.close();
          resolve();
        };
        transaction.onerror = () => {
          database.close();
          reject(transaction.error);
        };
        transaction.onabort = () => {
          database.close();
          reject(transaction.error);
        };
        request.onerror = () => {
          database.close();
          reject(request.error);
        };
      }).catch((error) => {
        reject(error);
      });
    });
  }

  public get<ExpectedType extends any>(key: string, defaultData: any = undefined): Promise<ExpectedType | undefined> {
    return new Promise<ExpectedType>((resolve, reject) => {
      this.getDatabase().then((database) => {
        if (!database.objectStoreNames.contains(this.objectStoreKey)) {
          database.close();
          resolve(defaultData);
          return;
        }

        const transaction = database.transaction(this.objectStoreKey, 'readonly');
        const store = transaction.objectStore(this.objectStoreKey);
        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result?.value || defaultData);
        };
        transaction.oncomplete = () => {
          database.close();
        };
        transaction.onerror = () => {
          database.close();
          reject(transaction.error);
        };
        transaction.onabort = () => {
          database.close();
          reject(transaction.error);
        };
        request.onerror = () => {
          database.close();
          reject(request.error);
        };
      }).catch((error) => {
        reject(error);
      });
    });
  }

  public getByPrefix<ExpectedType = unknown>(prefix: string): Promise<Record<string, ExpectedType>> {
    return new Promise((resolve, reject) => {
      this.getDatabase()
        .then((database) => {
          if (!database.objectStoreNames.contains(this.objectStoreKey)) {
            database.close();
            resolve({});
            return;
          }

          const transaction = database.transaction(this.objectStoreKey, 'readonly');
          const store = transaction.objectStore(this.objectStoreKey);

          const results: Record<string, ExpectedType> = {};

          // Most compatible: cursor + manual prefix check
          const request = store.openCursor();

          request.onsuccess = () => {
            const cursor = request.result as IDBCursorWithValue | null;
            if (!cursor) return;

            const record = cursor.value as { key: string; value: ExpectedType };
            if (typeof record?.key === 'string' && record.key.startsWith(prefix)) {
              results[record.key] = record.value;
            }

            cursor.continue();
          };

          request.onerror = () => {
            database.close();
            reject(request.error);
          };

          transaction.oncomplete = () => {
            database.close();
            resolve(results);
          };

          transaction.onerror = () => {
            database.close();
            reject(transaction.error);
          };

          transaction.onabort = () => {
            database.close();
            reject(transaction.error);
          };
        })
        .catch(reject);
    });
  }

  public delete(key: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.getDatabase().then((database) => {
        if (!database.objectStoreNames.contains(this.objectStoreKey)) {
          database.close();
          resolve(); // oder reject(new Error(...))
          return;
        }

        const transaction = database.transaction(this.objectStoreKey, 'readwrite');
        const store = transaction.objectStore(this.objectStoreKey);
        const request = store.delete(key);

        transaction.oncomplete = () => {
          database.close();
          resolve();
        };
        transaction.onerror = () => {
          database.close();
          reject(transaction.error);
        };
        transaction.onabort = () => {
          database.close();
          reject(transaction.error);
        };
        request.onerror = () => {
          database.close();
          reject(request.error);
        };
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Clears all entries from the object store.
   * Fastest way to remove everything without deleting the whole database.
   */
  public clear(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.getDatabase()
        .then((database) => {
          // Store existiert nicht? -> nichts zu tun
          if (!database.objectStoreNames.contains(this.objectStoreKey)) {
            database.close();
            resolve();
            return;
          }

          try {
            const transaction = database.transaction(this.objectStoreKey, 'readwrite');
            const store = transaction.objectStore(this.objectStoreKey);
            const request = store.clear();

            transaction.oncomplete = () => {
              database.close();
              resolve();
            };

            transaction.onerror = () => {
              database.close();
              reject(transaction.error);
            };
            transaction.onabort = () => {
              database.close();
              reject(transaction.error);
            };
            request.onerror = () => {
              database.close();
              reject(request.error);
            };
          } catch (e) {
            database.close();
            resolve();
          }
        })
        .catch(reject);
    });
  }
}

const appIDB = new AppIDB();

const deleteDatabase = (dbName: string): Promise<'deleted' | 'blocked'> => new Promise((resolve, reject) => {
  const req = window.indexedDB.deleteDatabase(dbName);

  req.onsuccess = () => resolve('deleted');
  req.onerror = () => reject(req.error);
  req.onblocked = () => resolve('blocked');
});

const tryDeleteLegacyDB = async () => {
  const result = await deleteDatabase('ioAppStorage').catch(() => 'blocked' as const);
  if (result === 'deleted') return;

  await new Promise((r) => {
    setTimeout(r, 2000);
  });

  await tryDeleteLegacyDB();
};

export const migrateAndDeleteLegacyIDB = async (): Promise<void> => {
  const alreadyMigrated = await appIDB.get<boolean>('migrated', false);
  const legacyAppIDB = new AppIDB('keyValuePairs', 'ioAppStorage', 1);

  if (alreadyMigrated) {
    await legacyAppIDB.clear();

    return;
  }

  const valuesToMigrate: Record<string, unknown> = await legacyAppIDB.getByPrefix('table.');
  const keys = Object.keys(valuesToMigrate);

  if (!keys.length) {
    await legacyAppIDB.clear();
    await appIDB.set('migrated', true);
    await tryDeleteLegacyDB();

    return;
  }

  const allUpdates: Promise<unknown>[] = [];
  keys.forEach((key) => {
    const value = valuesToMigrate[key];

    allUpdates.push(appIDB.set(key, value));
  });

  Promise.all(allUpdates).then(async () => {
    await legacyAppIDB.clear();
    await appIDB.set('migrated', true);
    await tryDeleteLegacyDB();
  });
};

export default appIDB;
